import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getCompanyInfo, getDashboardOverview, getPublicPricingPlans } from '../services/api';
import { uniquePlanFeatures } from '../utils/planFeatures';
import './Company.css';
import './Upgrade.css';

const truthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const planLabel = (key, t) => {
  const k = String(key || 'free').toLowerCase();
  if (k === 'premium' || k === 'pro') return t('upgrade.plans.pro', 'Pro');
  if (k === 'basic') return t('upgrade.plans.basic', 'Basic');
  if (k === 'enterprise') return t('upgrade.plans.enterprise', 'Enterprise');
  if (k === 'free') return t('upgrade.plans.free', 'Free');
  return k.charAt(0).toUpperCase() + k.slice(1);
};

const normalizeKey = (key) =>
  String(key || '')
    .toLowerCase()
    .replace('premium', 'pro')
    .trim();

const Upgrade = () => {
  const { t } = useTranslation();
  const { user, isCompanyAdmin } = useAuth();

  const companyAdmin = useMemo(
    () =>
      truthyFlag(isCompanyAdmin) ||
      truthyFlag(user?.is_company_admin) ||
      truthyFlag(localStorage.getItem('isCompanyAdmin')),
    [isCompanyAdmin, user]
  );

  const [loading, setLoading] = useState(true);
  const [planKey, setPlanKey] = useState('free');
  const [companyName, setCompanyName] = useState('');
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [overview, pricing, companyInfo] = await Promise.all([
          getDashboardOverview().catch(() => null),
          getPublicPricingPlans().catch(() => []),
          companyAdmin ? getCompanyInfo().catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const fromLicense = companyInfo?.license?.plan_type;
        const fromOverview = overview?.plan_type;
        setPlanKey(String(fromLicense || fromOverview || 'free').toLowerCase());
        setCompanyName(companyInfo?.company?.name || user?.company_name || '');
        setPlans(Array.isArray(pricing) ? pricing : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyAdmin, user?.company_name]);

  const currentPlanKey = normalizeKey(planKey);

  const fallbackPlans = [
    { plan_key: 'FREE', display_name: 'Free', price_monthly: 0, currency: 'TRY', features: [] },
    { plan_key: 'BASIC', display_name: 'Basic', price_monthly: 99, currency: 'TRY', features: [] },
    {
      plan_key: 'PREMIUM',
      display_name: 'Premium',
      price_monthly: 299,
      currency: 'TRY',
      features: [],
    },
    {
      plan_key: 'ENTERPRISE',
      display_name: 'Enterprise',
      price_monthly: 0,
      currency: 'TRY',
      features: [],
    },
  ];

  if (loading) {
    return (
      <div className="co-page up-page">
        <div className="co-empty">
          <div className="co-spinner" />
          <p>{t('upgrade.loading', 'Loading plan…')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="co-page up-page">
      <section className="co-hero up-hero">
        <div className="co-hero-copy">
          <p className="co-kicker">{t('upgrade.kicker', 'Company package')}</p>
          <h1>{t('upgrade.title', 'Premium package')}</h1>
          <p className="co-lead up-lead-desktop">
            {companyAdmin
              ? t(
                  'upgrade.leadAdmin',
                  'Pick a plan, complete the demo checkout, and your company upgrades instantly with an invoice.'
                )
              : t(
                  'upgrade.leadMember',
                  'Only a user with company management permission can upgrade this package.'
                )}
          </p>
          <p className="co-lead up-lead-mobile">
            {companyAdmin
              ? t('upgrade.leadAdminShort', 'Pick a plan → demo pay → upgrade.')
              : t('upgrade.leadMemberShort', 'Only a company admin can upgrade.')}
          </p>
          <div className="up-current">
            <span>{t('upgrade.current', 'Current plan')}</span>
            <strong>{planLabel(currentPlanKey, t)}</strong>
            {companyName ? <em>{companyName}</em> : null}
          </div>
        </div>
      </section>

      {!companyAdmin && (
        <div className="up-locked" role="status">
          <div className="up-locked-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V8a4 4 0 018 0v3" />
            </svg>
          </div>
          <h2>{t('upgrade.lockedTitle', 'Upgrade not available')}</h2>
          <p>
            {t(
              'upgrade.lockedBody',
              'Someone with company management permission can change the company package. Ask your company admin to upgrade.'
            )}
          </p>
        </div>
      )}

      {companyAdmin && (
        <>
          <div className="co-plans up-plans">
            {(plans.length ? plans : fallbackPlans).map((plan) => {
              const key = normalizeKey(plan.plan_key);
              const isCurrent = key === currentPlanKey;
              const isFree = key === 'free';
              return (
                <article
                  key={plan.plan_key || key}
                  className={`co-plan-card up-plan-card ${isCurrent ? 'is-current' : ''}`}
                >
                  {isCurrent && (
                    <span className="co-plan-badge">{t('company.upgrade.current', 'Current plan')}</span>
                  )}
                  <h3>{plan.display_name || planLabel(key, t)}</h3>
                  <p className="co-plan-price">
                    {plan.price_monthly > 0
                      ? `${plan.price_monthly} ${plan.currency || 'TRY'}/mo`
                      : isFree
                        ? t('upgrade.plans.free', 'Free')
                        : t('company.plan.contact', 'Contact us')}
                  </p>
                  <ul>
                    <li>
                      {plan.max_users > 0
                        ? `${plan.max_users} ${t('company.plan.users', 'users')}`
                        : t('company.plan.unlimitedUsers', 'Unlimited users')}
                    </li>
                    <li>
                      {plan.max_connections > 0
                        ? `${plan.max_connections} ${t('company.plan.connections', 'connections')}`
                        : t('company.plan.unlimitedConnections', 'Unlimited connections')}
                    </li>
                    {uniquePlanFeatures(plan).map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <button type="button" className="co-btn ghost" disabled>
                      {t('company.upgrade.current', 'Current plan')}
                    </button>
                  ) : isFree ? (
                    <button type="button" className="co-btn ghost" disabled>
                      {t('upgrade.plans.free', 'Free')}
                    </button>
                  ) : (
                    <Link
                      className="co-btn primary"
                      to={`/dashboard/checkout?plan=${encodeURIComponent(key)}`}
                    >
                      {t('upgrade.buy', 'Buy & upgrade')}
                    </Link>
                  )}
                </article>
              );
            })}
          </div>
          <p className="co-note center up-note">
            {t(
              'upgrade.demoNote',
              'Demo payment: cards are not charged. After pay, an invoice is created and the plan activates immediately.'
            )}
          </p>
        </>
      )}
    </div>
  );
};

export default Upgrade;
